import React, { useState, useRef } from "react";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { FileText, Download, Pencil, X, Plus, Paperclip, FileSignature, CheckCircle2, Clock3, Link2, ExternalLink, Upload } from "lucide-react";
import { isLinkDoc, isValidUrl, LinkProviderBadge } from "../shared/LinkDocDialog";

function formatSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

const DOC_TYPE_LABELS = { remise_signee: "Remise signée", restitution_signee: "Restitution signée" };

export function SignedBadges({ attachments = [], itemStatus, itemId, shared = false }) {
    const remise = attachments.find((a) => a.doc_type === "remise_signee");
    const restit = attachments.find((a) => a.doc_type === "restitution_signee");
    return (
        <span className="inline-flex items-center gap-1.5 flex-wrap">
            {remise ? (
                <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200" data-testid={`signed-remise-badge-${itemId}`}>
                    <CheckCircle2 className="w-2.5 h-2.5" />Remise signée le {new Date(remise.signed_date).toLocaleDateString("fr-CH")}
                </span>
            ) : itemStatus === "assigned" && !shared ? (
                <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`signed-remise-pending-${itemId}`}>
                    <Clock3 className="w-2.5 h-2.5" />Remise à signer
                </span>
            ) : null}
            {restit && (
                <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200" data-testid={`signed-restitution-badge-${itemId}`}>
                    <CheckCircle2 className="w-2.5 h-2.5" />Restitution signée le {new Date(restit.signed_date).toLocaleDateString("fr-CH")}
                </span>
            )}
        </span>
    );
}

export function ItemDocuments({ itemId, attachments = [], onChanged, compact = false, itemStatus, shared = false }) {
    const [formOpen, setFormOpen] = useState(!compact);
    const [mode, setMode] = useState("file");
    const [name, setName] = useState("");
    const [file, setFile] = useState(null);
    const [url, setUrl] = useState("");
    const [docType, setDocType] = useState("");
    const [signedDate, setSignedDate] = useState(new Date().toISOString().slice(0, 10));
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const resetForm = () => {
        setName("");
        setFile(null);
        setUrl("");
        setDocType("");
        if (fileRef.current) fileRef.current.value = "";
        if (compact) setFormOpen(false);
    };

    const handleUpload = async () => {
        if (mode === "file" && !file) { toast.error("Sélectionnez un fichier"); return; }
        if (mode === "link" && !isValidUrl(url)) { toast.error("URL invalide (http:// ou https://)"); return; }
        setUploading(true);
        try {
            if (mode === "link") {
                await api.addInventoryLink(itemId, { url: url.trim(), display_name: name.trim(), ...(docType ? { doc_type: docType, signed_date: signedDate } : {}) });
            } else {
                await api.uploadInventoryAttachment(itemId, file, name.trim(), docType ? { docType, signedDate } : {});
            }
            toast.success(docType ? `${DOC_TYPE_LABELS[docType]} enregistrée (${new Date(signedDate).toLocaleDateString("fr-CH")})` : mode === "link" ? "Lien ajouté" : "Document ajouté");
            resetForm();
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
        finally { setUploading(false); }
    };

    const handleRename = async (att) => {
        const newName = window.prompt("Nouveau nom du document :", att.display_name || att.filename);
        if (newName === null || !newName.trim()) return;
        try {
            await api.renameInventoryAttachment(att.id, newName.trim());
            toast.success("Document renommé");
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleDelete = async (att) => {
        if (!window.confirm(`Supprimer le document "${att.display_name || att.filename}" ?`)) return;
        try {
            await api.deleteInventoryAttachment(att.id);
            toast.success("Document supprimé");
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    return (
        <div data-testid={`item-documents-${itemId}`}>
            <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500">
                    <Paperclip className="w-3 h-3" />Documents {attachments.length > 0 && `(${attachments.length})`}
                    <SignedBadges attachments={attachments} itemStatus={itemStatus} itemId={itemId} shared={shared} />
                </span>
                {compact && (
                    <button onClick={() => setFormOpen(!formOpen)} className="inline-flex items-center gap-0.5 text-[11px] text-teal-600 hover:underline" data-testid={`add-doc-toggle-${itemId}`}>
                        <Plus className="w-3 h-3" />{formOpen ? "Fermer" : "Ajouter"}
                    </button>
                )}
            </div>

            {attachments.length === 0 && !formOpen && <p className="text-[10px] text-gray-300">Aucun document</p>}

            <div className="space-y-1">
                {attachments.map((att) => {
                    const link = isLinkDoc(att);
                    const href = link ? att.url : api.inventoryAttachmentUrl(att.id);
                    return (
                    <div key={att.id} className="flex items-center gap-2 px-2 py-1.5 rounded bg-gray-50 group" data-testid={`doc-row-${att.id}`}>
                        {att.doc_type ? <FileSignature className="w-3.5 h-3.5 text-green-600 flex-shrink-0" /> : link ? <Link2 className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" /> : <FileText className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                        <a href={href} target="_blank" rel="noreferrer"
                            className="text-xs text-gray-700 hover:text-teal-600 hover:underline truncate flex-1 min-w-0"
                            title={link ? att.url : att.filename} data-testid={`doc-link-${att.id}`}>
                            {att.display_name || att.filename}
                        </a>
                        {att.doc_type && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${att.doc_type === "remise_signee" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}>
                                {DOC_TYPE_LABELS[att.doc_type]}{att.signed_date ? ` · ${new Date(att.signed_date).toLocaleDateString("fr-CH")}` : ""}
                            </span>
                        )}
                        {link ? <LinkProviderBadge url={att.url} /> : <span className="text-[9px] text-gray-400 flex-shrink-0">{formatSize(att.size)}</span>}
                        <a href={href} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-teal-600" title={link ? "Ouvrir le lien" : "Télécharger"} data-testid={`doc-open-${att.id}`}>
                            {link ? <ExternalLink className="w-3 h-3" /> : <Download className="w-3 h-3" />}
                        </a>
                        <button onClick={() => handleRename(att)} className="text-gray-400 hover:text-blue-500" title="Renommer" data-testid={`doc-rename-${att.id}`}>
                            <Pencil className="w-3 h-3" />
                        </button>
                        <button onClick={() => handleDelete(att)} className="text-gray-400 hover:text-red-500" title="Supprimer" data-testid={`doc-delete-${att.id}`}>
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                    );
                })}
            </div>

            {formOpen && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <div className="inline-flex rounded-md border overflow-hidden h-8" data-testid={`doc-mode-toggle-${itemId}`}>
                        <button type="button" onClick={() => setMode("file")} className={`px-2 text-[11px] inline-flex items-center gap-1 ${mode === "file" ? "bg-teal-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`} title="Envoyer un fichier" data-testid={`doc-mode-file-${itemId}`}><Upload className="w-3 h-3" />Fichier</button>
                        <button type="button" onClick={() => setMode("link")} className={`px-2 text-[11px] inline-flex items-center gap-1 border-l ${mode === "link" ? "bg-sky-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`} title="Lien OneDrive / SharePoint" data-testid={`doc-mode-link-${itemId}`}><Link2 className="w-3 h-3" />Lien</button>
                    </div>
                    <select value={docType} onChange={(e) => setDocType(e.target.value)}
                        className="h-8 px-2 text-xs border rounded-md text-gray-700" data-testid={`doc-type-select-${itemId}`}>
                        <option value="">Document</option>
                        <option value="remise_signee">Remise signée</option>
                        <option value="restitution_signee">Restitution signée</option>
                    </select>
                    {docType && (
                        <input type="date" value={signedDate} onChange={(e) => setSignedDate(e.target.value)}
                            className="h-8 px-2 text-xs border rounded-md text-gray-700" title="Date de signature" data-testid={`doc-signed-date-${itemId}`} />
                    )}
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={docType ? "Nom (optionnel)" : "Nom du document (ex: Facture d'achat)"} className="flex-1 min-w-[120px] h-8 text-xs" data-testid={`doc-name-input-${itemId}`} />
                    {mode === "link" ? (
                        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… (OneDrive, SharePoint…)" className="flex-1 min-w-[180px] h-8 text-xs font-mono" data-testid={`doc-url-input-${itemId}`} />
                    ) : (
                        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="text-[11px] text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-[11px] file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 max-w-[200px]"
                            data-testid={`doc-file-input-${itemId}`} />
                    )}
                    <Button size="sm" className="h-8" onClick={handleUpload} disabled={uploading || (mode === "file" ? !file : !isValidUrl(url))} data-testid={`doc-upload-btn-${itemId}`}>
                        {uploading ? "..." : "Ajouter"}
                    </Button>
                </div>
            )}
        </div>
    );
}
